import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { LaTeXRenderer } from './LaTeXRenderer';

interface MarkdownWithLaTeXProps {
  children: string;
  components?: any;
  [key: string]: any;
}

export const MarkdownWithLaTeX: React.FC<MarkdownWithLaTeXProps> = ({ 
  children, 
  components = {}, 
  ...props 
}) => {
  // Enhanced components that handle LaTeX in code blocks
  const enhancedComponents = {
    ...components,
    
    // Override pre component to detect LaTeX in code blocks
    pre: ({ children, ...props }: any) => {
      // Check if this is a code block with LaTeX content
      const codeElement = React.Children.toArray(children).find(
        (child: any) => child?.type === 'code'
      ) as any;
      
      if (codeElement && codeElement.props?.children) {
        const codeContent = String(codeElement.props.children).replace(/\n$/, '');
        
        // Check if this looks like LaTeX
        const isDisplayMath = codeContent.startsWith('$$') && codeContent.endsWith('$$');
        const hasLatexCommands = /\\[a-zA-Z]+/.test(codeContent);
        
        if (isDisplayMath) {
          const latex = codeContent.slice(2, -2).trim();
          return (
            <div className="my-4 text-center">
              <LaTeXRenderer displayMode={true}>{latex}</LaTeXRenderer>
            </div>
          );
        }
        
        if (hasLatexCommands && !codeElement.props?.className?.includes('language-')) {
          return (
            <div className="my-4 text-center">
              <LaTeXRenderer displayMode={true}>{codeContent}</LaTeXRenderer>
            </div>
          );
        }
      }
      
      // Fall back to original pre component
      if (components.pre) {
        return components.pre({ children, ...props });
      }
      
      return <pre {...props}>{children}</pre>;
    },

    // Override code component to detect and render LaTeX
    code: ({ children, className, ...props }: any) => {
      const codeContent = String(children).replace(/\n$/, '');
      
      // Only process if it's not inside a pre block (inline code)
      if (!className) {
        const isInlineMath = codeContent.startsWith('$') && codeContent.endsWith('$') && !codeContent.includes('\n');
        
        if (isInlineMath && codeContent.length > 2) {
          const latex = codeContent.slice(1, -1).trim();
          return <LaTeXRenderer displayMode={false}>{latex}</LaTeXRenderer>;
        }
      }
      
      // Fall back to original code component
      if (components.code) {
        return components.code({ children, className, ...props });
      }
      
      return (
        <code className="rounded bg-gray-100 px-1.5 py-0.5 text-sm font-mono text-gray-800 dark:bg-gray-800 dark:text-gray-200" {...props}>
          {children}
        </code>
      );
    },

    // Also handle LaTeX in text content
    p: ({ children, ...props }: any) => {
      const processedChildren = React.Children.map(children, (child, index) => {
        if (typeof child === 'string') {
          return processTextWithLaTeX(child, index);
        }
        return child;
      });

      return <p {...props}>{processedChildren}</p>;
    },

    // Handle LaTeX in other text containers
    li: ({ children, ...props }: any) => {
      const processedChildren = React.Children.map(children, (child, index) => {
        if (typeof child === 'string') {
          return processTextWithLaTeX(child, index);
        }
        return child;
      });

      return <li {...props}>{processedChildren}</li>;
    },

    h1: ({ children, ...props }: any) => {
      const processedChildren = React.Children.map(children, (child, index) => {
        if (typeof child === 'string') {
          return processTextWithLaTeX(child, index);
        }
        return child;
      });

      return <h1 {...props}>{processedChildren}</h1>;
    },

    h2: ({ children, ...props }: any) => {
      const processedChildren = React.Children.map(children, (child, index) => {
        if (typeof child === 'string') {
          return processTextWithLaTeX(child, index);
        }
        return child;
      });

      return <h2 {...props}>{processedChildren}</h2>;
    },

    h3: ({ children, ...props }: any) => {
      const processedChildren = React.Children.map(children, (child, index) => {
        if (typeof child === 'string') {
          return processTextWithLaTeX(child, index);
        }
        return child;
      });

      return <h3 {...props}>{processedChildren}</h3>;
    },
  };

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={enhancedComponents}
      {...props}
    >
      {children}
    </ReactMarkdown>
  );
};

// Helper function to process LaTeX in regular text
function processTextWithLaTeX(text: string, keyBase: number) {
  // Don't process if there are no $ signs
  if (!text.includes('$')) {
    return text;
  }

  const parts: (string | React.ReactElement)[] = [];
  const remaining = text;
  let counter = 0;

  // Process display math ($$...$$)
  const displayMathRegex = /\$\$([\s\S]*?)\$\$/g;
  let lastIndex = 0;
  let match;

  while ((match = displayMathRegex.exec(remaining)) !== null) {
    // Add text before the math
    if (match.index > lastIndex) {
      const textBefore = remaining.slice(lastIndex, match.index);
      if (textBefore) {
        parts.push(textBefore);
      }
    }

    // Add the display math
    parts.push(
      <div key={`${keyBase}-display-${counter++}`} className="my-4 text-center">
        <LaTeXRenderer displayMode={true}>{match[1].trim()}</LaTeXRenderer>
      </div>
    );

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  const remainingText = remaining.slice(lastIndex);

  // Process inline math in the remaining text
  if (remainingText.includes('$')) {
    const inlineParts: (string | React.ReactElement)[] = [];
    const inlineMathRegex = /\$([^$\n]+?)\$/g;
    let inlineLastIndex = 0;
    let inlineMatch;

    while ((inlineMatch = inlineMathRegex.exec(remainingText)) !== null) {
      // Add text before the inline math
      if (inlineMatch.index > inlineLastIndex) {
        const textBefore = remainingText.slice(inlineLastIndex, inlineMatch.index);
        if (textBefore) {
          inlineParts.push(textBefore);
        }
      }

      // Add the inline math
      inlineParts.push(
        <LaTeXRenderer key={`${keyBase}-inline-${counter++}`} displayMode={false}>
          {inlineMatch[1].trim()}
        </LaTeXRenderer>
      );

      inlineLastIndex = inlineMatch.index + inlineMatch[0].length;
    }

    // Add remaining text
    if (inlineLastIndex < remainingText.length) {
      const finalText = remainingText.slice(inlineLastIndex);
      if (finalText) {
        inlineParts.push(finalText);
      }
    }

    parts.push(...inlineParts);
  } else {
    if (remainingText) {
      parts.push(remainingText);
    }
  }

  // If no LaTeX was found, return original text
  if (parts.length === 0) {
    return text;
  }

  // If only one part and it's a string, return it directly
  if (parts.length === 1 && typeof parts[0] === 'string') {
    return parts[0];
  }

  return <>{parts}</>;
}